import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* DESIGN.md §3 : « Le rayon est de 5px. Sur tout. » `rounded-control` lit
   `--radius` (src/os/os.css `:root`, global) — l'alias legacy `rounded-btn`
   valait 3px.

   L'arbitrage redouté avec le marketing public n'a pas lieu : aucune des huit
   pages publiques n'importe ce primitif. Ses 17 consommateurs sont tous dans
   le produit connecté (settings/, tier/, billing/, layout/Topbar, les pages
   Dashboard/Billing/Settings/Checkout). Les pages publiques sont habillées par
   src/styles/*.css, qui a son propre `--radius-*`. Re-grepper avant de
   supposer l'inverse.

   Les tailles sm/lg ne redéclarent plus de rayon : il n'y en a qu'un. */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-control text-sm font-medium tracking-[0.01em] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:translate-y-[-1px] active:scale-[0.98]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-ink-black hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-card hover:bg-muted hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-signal-ink underline-offset-4 hover:underline hover:translate-y-0',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
